import * as React from "react";
import { Label, Link, mergeStyles, mergeStyleSets, Stack, Stylesheet } from "@fluentui/react";
import { buildTestId } from "../../helpers/testIdHelper";
import { LabelInfoTooltip, RequiredTooltip } from "../Tooltip";
import { BaseInputProps } from "./BaseInput";
import { FormInputGroupLayout } from "./Form";
import {
  baseLabelStyles,
  iconClass,
  linkStylesCompact,
  linkStylesWide,
  renderErrorMessages,
  renderStatusBar,
  stackItemStyles,
  subFieldBorder,
} from "./InputCommon";

const defaultComponentStyle = mergeStyleSets({ container: { width: "100%", minWidth: "107px" } });

const stackItemStylesWide = { root: { minWidth: "245px" } };

const subFieldStyles = { root: { paddingLeft: "5px" } };
const labelSubFieldStyles = mergeStyleSets(baseLabelStyles, subFieldStyles);

const subFieldSeperator = <span style={subFieldBorder}>{ }</span>;

type BaseLabelProps = Pick<BaseInputProps,
"disabled"
| "fieldName"
| "inputLink"
| "required"
| "statusInfo"
| "subField"
| "tooltip"
>;

export interface LabelWrapperProps extends BaseLabelProps {
  /**
   * The input component to be displayed
   */
  children: JSX.Element;
  /**
   * ClassName styles to pass to label wrapper
   * NOTE: Only applied when the FormInputGroupLayout is COMPACT
   */
  className?: string;
  /**
   * The layout of the component
   */
  layout: FormInputGroupLayout;
  /**
   * The list of errors determined by the validators
   */
  errors: string[];
  /**
   * The text to display in the label
   */
  label?: string;
  /**
   * Allows override of whether the label and the input are displayed on the same row
   * @default true when the FormInputGroupLayout is set to WIDE
   * @default false when the FormInputGroupLayout is set to COMPACT
   */
  horizontal?: boolean;
  /**
   * Reverse the order of the label and the input
   * @default false
   */
  reversed?: boolean;
  /**
   * derives multiple test ids for nested elements
   */
  testId?: string;
}

/**
 * It does not extend the ComponentTestIds interface as this is a 'wrapper' not a 'core' component
 */
export interface LabelWrapperTestIds {
  error: string;
  label: string;
}

export const buildLabelWrapperTestIds = (testId?: string): LabelWrapperTestIds => ({
  error: buildTestId(testId, "-error"),
  label: buildTestId(testId, "-label"),
});

export const LabelWrapper = ({
  errors,
  fieldName,
  layout,
  className,
  required,
  statusInfo,
  subField,
  tooltip,
  children,
  label,
  inputLink,
  horizontal,
  reversed,
  testId,
}: LabelWrapperProps): JSX.Element => {
  const isWideLayout = layout === FormInputGroupLayout.WIDE;
  const hasWideSubField = subField && isWideLayout;

  const labelWrapperTestIds = buildLabelWrapperTestIds(testId);

  const labelWrapperStyle = {
    width: isWideLayout ? "245px" : "100%",
    maxWidth: isWideLayout ? "245px" : "100%",
  };

  const getLabelWrapperClassName = (): string => {
    if (isWideLayout || !className) {
      return defaultComponentStyle.container;
    }

    const stylesheet = Stylesheet.getInstance();
    const consumerArgs = stylesheet.argsFromClassName(className);
    const internalArgs = stylesheet.argsFromClassName(defaultComponentStyle.container);

    // consumer args take precendence
    const mergedClassName = mergeStyles(internalArgs, consumerArgs);
    return mergedClassName;
  };

  // 53px is the width of the subField
  // plus its left margin
  // plus the padding between the label and the subField indicator
  // -> 45 + 3 + 5
  const labelFixedWidth = hasWideSubField ? "calc(100% - 53px)" : "100%";

  const labelStyle = {
    minWidth: labelFixedWidth,
    width: labelFixedWidth,
    maxWidth: labelFixedWidth,
  };

  const wrappedInputStyles = mergeStyleSets(
    inputLink === undefined ? stackItemStyles : undefined,
    { root: { paddingBottom: !inputLink ? "10px" : undefined } },
    { root: { justifyContent: reversed ? "flex-end" : "space-between" } },
    { root: { width: (subField && (!isWideLayout || reversed)) ? "calc(100% - 53px)" : "100%" } },
  );

  const link = (
    <Link
      data-test-id={inputLink?.testId}
      styles={
        layout === FormInputGroupLayout.COMPACT
          ? linkStylesCompact
          : linkStylesWide
      }
      onClick={() => { inputLink?.onLinkClick?.(); }}
    >
      <span id={inputLink?.id}>{inputLink?.text}</span>
    </Link>
  );

  return (
    <Stack className={getLabelWrapperClassName()}>
      <Stack horizontal tokens={{ childrenGap: "5px" }}>
        {subField && (!isWideLayout || reversed) && subFieldSeperator}
        <Stack
          tokens={{ childrenGap: "5px" }}
          reversed={reversed}
          horizontal={horizontal !== undefined ? horizontal : isWideLayout}
          styles={wrappedInputStyles}
        >
          {label && (
          <Stack
            horizontal
            styles={isWideLayout && !reversed ? stackItemStylesWide : undefined}
            data-test-id={labelWrapperTestIds.label}
          >
            <Stack
              horizontal
              style={labelWrapperStyle}
            >
              {hasWideSubField && !reversed && subFieldSeperator}
              <Label
                htmlFor={fieldName}
                styles={hasWideSubField && !reversed ? labelSubFieldStyles : baseLabelStyles}
                style={labelStyle}
              >
                {label}
                {required && <RequiredTooltip />}
                {tooltip !== undefined && (
                  <LabelInfoTooltip
                    tooltip={tooltip}
                    styles={!required || subField ? iconClass : undefined}
                  />
                )}
              </Label>
            </Stack>
          </Stack>
          )}
          {children}
        </Stack>
      </Stack>
      <Stack>
        {inputLink !== undefined && link}
        {errors.length > 0 && renderErrorMessages(errors, layout, labelWrapperTestIds.error)}
        {statusInfo ? renderStatusBar(statusInfo, layout) : null}
      </Stack>
    </Stack>
  );
};
